import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Building2, Handshake, TrendingUp, Award } from "lucide-react";
import { api } from "../lib/api";

const IMG = "https://images.pexels.com/photos/36712839/pexels-photo-36712839.jpeg?auto=compress&cs=tinysrgb&h=600&w=900";

export default function CSR() {
  const [form, setForm] = useState({ company_name: "", contact_person: "", email: "", phone: "", program_interest: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/csr/inquire", form);
      toast.success("Thank you! Our CSR team will reach out within 24 hours.");
      setForm({ company_name: "", contact_person: "", email: "", phone: "", program_interest: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fade-in">
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#138808] font-bold mb-2">CSR Partnership</div>
          <h1 className="serif text-4xl md:text-5xl font-semibold text-[#000080] leading-tight">
            Turn compliance into <em className="text-[#FF9933] not-italic">impact</em>.
          </h1>
          <p className="mt-5 text-lg text-[#4A4A4A]">
            Partner with AHCCT under the Companies Act 2013 CSR mandate. 
            Fund high-impact programs, track every rupee, and get SROI-ready reports.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            {[
              { icon: Award, title: "CSR Reg: CSR00103199", sub: "MCA-verified" },
              { icon: TrendingUp, title: "Annual SROI Reports", sub: "Transparent impact" },
              { icon: Handshake, title: "Co-branded Programs", sub: "Joint field visits" },
              { icon: Building2, title: "Pan-India Presence", sub: "Grassroots reach" },
            ].map((c, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#eae5d9] p-4">
                <c.icon className="w-5 h-5 text-[#FF9933] mb-2" strokeWidth={1.6} />
                <div className="text-sm font-bold text-[#000080]">{c.title}</div>
                <div className="text-xs text-[#737373]">{c.sub}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
          <img src={IMG} alt="Corporate Partnership" className="w-full h-full object-cover" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12">
        <div className="grid md:grid-cols-4 gap-4">
          {["Education Projects", "Healthcare Programs", "Environmental Campaigns", "Women Empowerment"].map((p, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-[#eae5d9] card-hover" data-testid={`csr-program-${i}`}>
              <div className="serif text-xl font-semibold text-[#000080]">{p}</div>
              <div className="text-sm text-[#737373] mt-2">Ready-to-deploy CSR-aligned project deck available on request.</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 md:px-6 py-12">
        <div className="bg-white rounded-3xl border border-[#eae5d9] p-8 md:p-10 shadow-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-[#FF9933] font-bold mb-2">Get in touch</div>
          <h2 className="serif text-3xl font-semibold text-[#000080] mb-6">Start a CSR Partnership</h2>
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-4" data-testid="csr-form">
            <Input required placeholder="Company Name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="csr-company" />
            <Input required placeholder="Contact Person" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="csr-contact" />
            <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="csr-email" />
            <Input required placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="csr-phone" />
            <Select value={form.program_interest} onValueChange={(v) => setForm({ ...form, program_interest: v })}>
              <SelectTrigger data-testid="csr-interest" className="md:col-span-2"><SelectValue placeholder="Program of Interest" /></SelectTrigger>
              <SelectContent>
                {["Education", "Healthcare", "Environment", "Women Empowerment", "Multi-program"].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea placeholder="Tell us about your CSR goals..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="md:col-span-2 min-h-[120px]" data-testid="csr-message" />
            <Button disabled={loading} className="md:col-span-2 bg-[#FF9933] hover:bg-[#E68A2E] text-white h-12 rounded-full btn-glow" data-testid="csr-submit">
              {loading ? "Sending..." : "Request Partnership Proposal"}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
